import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { LineChart } from "@mui/x-charts/LineChart";
import api from "../../helpers/api";

function AreaGradient({ color, id }: { color: string; id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

interface DataHistory {
  id: number;
  url: string;
  ticketQuantity: number;
  lowestPrice: number;
  scrapedAt: string;
  listingCount: number | null;
}

interface Props {
  url: string;
  quantity: number;
  sectionFilter: string[];
  mode: "price" | "quantity";
}

const DataOverTimeChart: React.FC<Props> = ({
  url,
  quantity,
  sectionFilter,
  mode,
}) => {
  const [loading, setLoading] = useState(false);
  const [dataHistory, setDataHistory] = useState<DataHistory[]>([]);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      setDataHistory([]);
      if (!url) return;
      setLoading(true);
      try {
        const res = await api.post("/analytics/dataovertime", {
          url,
          quantity,
          sectionFilter,
        });
        setDataHistory(res.data);
      } catch (error) {
        console.error("Failed to fetch price history:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [url, quantity]);

  const processedData = React.useMemo(() => {
    if (dataHistory.length === 0) return { dates: [], values: [] };

    const dates = dataHistory.map((entry) => new Date(entry.scrapedAt));
    const oldestDate = new Date(Math.min(...dates.map((d) => d.getTime())));
    const newestDate = new Date(Math.max(...dates.map((d) => d.getTime())));

    const dateMap = new Map<string, number>();
    dataHistory.forEach((entry) => {
      const date = new Date(entry.scrapedAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const value =
        mode === "price" ? entry.lowestPrice : entry.listingCount || 0;
      const currentValue = dateMap.get(date);
      if (
        !currentValue ||
        (mode === "price" ? value < currentValue : value > currentValue)
      ) {
        dateMap.set(date, value);
      }
    });

    const allDates: string[] = [];
    const allValues: (number | null)[] = [];
    const currentDate = new Date(oldestDate);

    while (currentDate <= newestDate) {
      const dateStr = currentDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      allDates.push(dateStr);
      allValues.push(dateMap.get(dateStr) || null);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return {
      dates: allDates,
      values: allValues,
    };
  }, [dataHistory, mode]);

  if (loading) {
    return <div>Loading chart...</div>;
  }

  if (!url) {
    return null;
  }

  if (dataHistory.length < 3) {
    return (
      <Card variant="outlined" sx={{ width: "100%" }}>
        <CardContent>
          <Typography variant="body1" color="text.secondary" align="center">
            Analytics requires at least 3 days of data. Please check back later.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const colorPalette = [theme.palette.primary.main];
  return (
    <Card variant="outlined" sx={{ width: "100%" }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack sx={{ justifyContent: "space-between" }}>
            <Stack
              direction="row"
              sx={{
                alignContent: { xs: "center", sm: "flex-start" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="h4" component="p">
                {mode === "price"
                  ? "Lowest Price Over Time"
                  : "Available Listings Over Time"}
              </Typography>
              <Chip
                size="small"
                color={
                  mode === "price"
                    ? (processedData.values[0] ?? 0) <
                      (processedData.values[processedData.values.length - 1] ??
                        0)
                      ? "error"
                      : "success"
                    : (processedData.values[0] ?? 0) >
                      (processedData.values[processedData.values.length - 1] ??
                        0)
                    ? "error"
                    : "success"
                }
                label={`${(
                  (((processedData.values[processedData.values.length - 1] ??
                    0) -
                    (processedData.values[0] ?? 0)) /
                    (processedData.values[0] ?? 1)) *
                  100
                ).toFixed(1)}%`}
              />
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: "#d7cdcb",
                fontSize: "1.1rem",
                fontStyle: "italic",
              }}
            >
              {quantity} tickets
              {" ● "}
              Sections:{" "}
              {sectionFilter && sectionFilter.length > 0
                ? sectionFilter.join(", ")
                : "Any"}
            </Typography>
          </Stack>
          <LineChart
            colors={colorPalette}
            xAxis={[
              {
                scaleType: "point",
                data: processedData.dates,
                tickLabelStyle: {
                  textAnchor: "start",
                },
                tickInterval: (index, i) => (i + 1) % 2 === 0,
                tickSize: 8,
              },
            ]}
            series={[
              {
                id: "dataLine",
                label: mode === "price" ? "Lowest Price" : "Available Listings",
                showMark: true,
                curve: "linear",
                area: true,
                data: processedData.values,
                connectNulls: true,
              },
            ]}
            height={250}
            margin={{ left: 40, right: 40, top: 20, bottom: 20 }}
            grid={{ horizontal: true }}
            sx={{
              "& .MuiAreaElement-series-dataLine": {
                fill: "url('#dataLine')",
              },
            }}
            slotProps={{
              legend: {
                hidden: true,
              },
            }}
          >
            <AreaGradient color={theme.palette.primary.main} id="dataLine" />
          </LineChart>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DataOverTimeChart;
